import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Typography,
  Button,
  Space,
  message,
  Spin,
  Tag,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Divider,
  Tabs,
  List,
  Avatar,
  Checkbox,
  Empty,
  Dropdown,
  Menu,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FilterOutlined,
  LineChartOutlined,
  VideoCameraOutlined,
  CloudOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  getDevicesByProjectId,
  getProjectById,
  createDevice,
  updateDevice,
  deleteDevice,
  getFormConfigById,
  getDeviceLiveData,
  getYingshiVideoTokenByUid,
  getYingshiDevicesByAppKey,
  getYingshiCamerasByDeviceSerial,
  getDeviceActionsByArea,
  DeviceAction,
  getYingshiLiveAddress,
  getDeviceLogs,
} from "../../services/api";
import {
  DynamicForm,
  FormConfig,
  FormFieldConfig,
} from "../../components/DynamicForm";
import { DeviceForm } from "../../components/DeviceForm";
import { YingshiCamera } from "../../components/YingshiCloud";
import VideoPlayer from "../../components/VideoPlayer";
import "./styles.css";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Search } = Input;

// Local Device interface definition
interface Device {
  id: number;
  deviceCode: string;
  deviceName: string;
  data: any;
  isPay: number;
  project: number;
  addTime: string;
  deviceType: number;
  thirdType: string;
  area: number | { id: number; title: string };
  beian: string;
  exData: {
    driverName?: string;
    idcardNumber?: string;
    monitorKey?: string;
    channel?: any[];
    [key: string]: any;
  };
  deviceFactory: string;
  latitude: string;
  longitude: string;
  uid: string | null;
  price: number | null;
  isDel: number;
  remark: string | null;
}

// Local deviceTypeMap definition
const deviceTypeMap = {
  0: "塔吊",
  1: "扬尘",
  3: "视频",
  4: "卸料平台",
  5: "施工升降机",
  6: "围栏",
  7: "高支模",
  9: "考勤机",
};

interface DeviceFormValues {
  deviceCode: string;
  deviceName: string;
  deviceType: number;
  deviceFactory: string;
  thirdType: string;
  beian: string;
  latitude: string;
  longitude: string;
  price: number | null;
  isPay: number;
  remark: string | null;
  driverName: string;
  idcardNumber: string;
  monitorKey: string;
  [key: string]: any; // Allow for dynamic fields
}

// Fixed form fields configuration
const fixedFormFields: FormFieldConfig[] = [
  {
    key: "deviceCode",
    type: "input",
    label: "设备ID",
    required: true,
    placeholder: "请输入设备ID",
    rules: [{ required: true, message: "请输入设备ID" }],
  },
  {
    key: "deviceName",
    type: "input",
    label: "设备名称",
    required: true,
    placeholder: "请输入设备名称",
    rules: [{ required: true, message: "请输入设备名称" }],
  },
  {
    key: "deviceType",
    type: "select",
    label: "设备类型",
    required: true,
    placeholder: "请选择设备类型",
    options: Object.entries(deviceTypeMap).map(([key, value]) => ({
      label: value,
      value: parseInt(key),
    })),
    rules: [{ required: true, message: "请选择设备类型" }],
  },
  {
    key: "area",
    type: "number",
    label: "区域",
    required: false,
    placeholder: "请输入区域编号",
    props: { min: 0, disabled: true },
  },
];

// Fixed Form Component
const FixedDeviceForm: React.FC<{
  form: any;
  initialValues: Record<string, any>;
  onFinish: (values: Record<string, any>) => void;
  onCancel: () => void;
  isEditing: boolean;
  formConfig: FormConfig | null;
  onDeviceTypeChange: (value: number) => void;
}> = ({ form, initialValues, onFinish, onCancel, isEditing, formConfig, onDeviceTypeChange }) => {
  // Set form values when initialValues change
  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  return (
    <div className="form-section">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={initialValues}
        requiredMark={false}
        preserve={false}
      >
        
      
        <div className="form-section-header">
          <Divider orientation="center">基本信息</Divider>
        </div>
        <Row gutter={[16, 0]}>
          {fixedFormFields.map((field) => (
            <Col key={field.key} xs={24} sm={12} md={12}>
              <Form.Item
                name={field.key}
                label={field.label}
                rules={field.rules}
                required={field.required}
              >
                {field.type === "input" ? (
                  <Input placeholder={field.placeholder} maxLength={128} />
                ) : field.type === "select" && field.key === "deviceType" ? (
                  <Select 
                    placeholder={field.placeholder}
                    onChange={(value) => onDeviceTypeChange(value as number)}
                  >
                    {field.options?.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                ) : field.type === "select" ? (
                  <Select placeholder={field.placeholder}>
                    {field.options?.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                ) : field.type === "number" ? (
                  <InputNumber
                    placeholder={field.placeholder}
                    style={{ width: "100%" }}
                    {...field.props}
                  />
                ) : null}
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Extended Information Section - Only show if formConfig exists */}
        {formConfig && formConfig.fields.length > 0 && (
          <>
            <div className="form-section-header">
              <Divider orientation="center">扩展信息</Divider>
            </div>
            <Row gutter={[16, 0]}>
              {formConfig.fields
                .filter(
                  (field) =>
                    !fixedFormFields.some(
                      (fixedField) =>
                        fixedField.key === field.key ||
                        (field.key === "device_code" &&
                          fixedField.key === "deviceCode") ||
                        (field.key === "device_name" &&
                          fixedField.key === "deviceName") ||
                        (field.key === "device_type" &&
                          fixedField.key === "deviceType") ||
                        (field.key === "area" && fixedField.key === "area")
                    )
                )
                .map((field) => (
                  <Col key={field.key} xs={24} sm={12} md={12}>
                    <Form.Item
                      name={field.key}
                      label={field.label}
                      rules={field.rules}
                      required={field.required}
                    >
                      {renderFormItem(field)}
                    </Form.Item>
                  </Col>
                ))}
            </Row>
          </>
        )}

        {/* Remarks Section */}
        <Form.Item name="remark" label="备注">
          <Input.TextArea placeholder="请输入备注" rows={3} maxLength={255} />
        </Form.Item>

        {/* Form Actions */}
        <div className="form-actions">
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" htmlType="submit">
            {isEditing ? "更新" : "创建"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

// Helper function to render form items based on their type
const renderFormItem = (field: FormFieldConfig) => {
  const { type, placeholder, options, props = {} } = field;

  switch (type) {
    case "input":
      return <Input placeholder={placeholder} {...props} />;
    case "textarea":
      return <Input.TextArea placeholder={placeholder} rows={3} {...props} />;
    case "number":
      return (
        <InputNumber
          placeholder={placeholder}
          style={{ width: "100%" }}
          {...props}
        />
      );
    case "select":
      return (
        <Select placeholder={placeholder} {...props}>
          {options?.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      );
    default:
      return <Input placeholder={placeholder} {...props} />;
  }
};

// Update or add DeviceLog interface definition
interface DeviceLog {
  id?: number;
  // Keep existing fields for backwards compatibility
  logTime?: string;
  type?: string;
  content?: string;
  deviceCode?: string;
  // Add new fields from the updated API response
  device_code?: string;
  action?: string;
  data?: string | any;
  status?: number;
  add_time?: string;
}

const Devices: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState<Device[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<Device[]>([]);
  const [projectName, setProjectName] = useState<string>("");
  const [projectArea, setProjectArea] = useState<number | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("新增设备");
  const [form] = Form.useForm<DeviceFormValues>();
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedDeviceType, setSelectedDeviceType] = useState<number | null>(
    null
  );
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<Record<string, any>>({});
  const [liveDataModalVisible, setLiveDataModalVisible] = useState(false);
  const [liveDataLoading, setLiveDataLoading] = useState(false);
  const [liveData, setLiveData] = useState<any>(null);
  const [currentDevice, setCurrentDevice] = useState<Device | null>(null);
  const [projectRegion, setProjectRegion] = useState<string>("");
  const [selectedCameras, setSelectedCameras] = useState<YingshiCamera[]>([]);
  const [deviceActions, setDeviceActions] = useState<DeviceAction[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [videoStreamUrl, setVideoStreamUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<Device | null>(null);
  
  // Add state variables for logs
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [deviceLogs, setDeviceLogs] = useState<DeviceLog[]>([]);

  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();

  // Fetch device actions based on project area
  const fetchDeviceActions = async (area: number) => {
    if (!area) return;
    
    try {
      setActionsLoading(true);
      const response = await getDeviceActionsByArea(area);
      if (response.data.code === 0 && response.data.data) {
        setDeviceActions(response.data.data);
      } else {
        console.warn("获取设备操作菜单失败:", response.data.msg);
      }
    } catch (error) {
      console.error("获取设备操作菜单错误:", error);
    } finally {
      setActionsLoading(false);
    }
  };

  // Get project name and area from location state if available
  useEffect(() => {
    if (location.state && "projectName" in location.state) {
      setProjectName(location.state.projectName as string);
    }

    if (projectId) {
      // Fetch project details to get the name and area
      getProjectById(parseInt(projectId))
        .then((response) => {
          if (response.data.code === 0 && response.data.data) {
            setProjectName(response.data.data.name);

            // Get area and store it - ensure we extract the ID correctly whether it's an object or number
            const area = response.data.data.area;
            const areaId = typeof area === 'object' && area !== null ? 
              (area as {id: number}).id : 
              (typeof area === 'number' ? area : 0);
            
            console.log("Project area from API:", area);
            console.log("Extracted area ID:", areaId);
            
            // Set the area ID in state
            setProjectArea(areaId);
            
            // Get region and store it
            const region = response.data.data.region;
            setProjectRegion(region);
            
            // Fetch device actions based on project area
            if (areaId) {
              fetchDeviceActions(areaId);
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching project details:", error);
        });
    }
  }, [projectId, location.state]);

  // Fetch form configuration based on area
  const fetchFormConfig = async (area: number, deviceType: number = 0) => {
    try {
      setFormLoading(true);
      
      // Clear any existing timeout
      if ((window as any).fetchFormConfigTimeout) {
        clearTimeout((window as any).fetchFormConfigTimeout);
      }
      
      // Set a new timeout to debounce the API call
      (window as any).fetchFormConfigTimeout = setTimeout(async () => {
        // Include both area and device_type in the form ID
        const formId = `device_${area}_${deviceType}`;
        const response = await getFormConfigById(formId);
  
        if (response.data.code === 0 && response.data.data) {
          // Get the form config without modifying it
          const config = response.data.data;
          setFormConfig(config);
        } else {
          console.warn(`未找到区域 ${area} 设备类型 ${deviceType} 的表单配置，将使用默认表单`);
          setFormConfig(null);
        }
        setFormLoading(false);
      }, 300); // 300ms debounce
    } catch (error) {
      console.error("Error fetching form configuration:", error);
      setFormConfig(null);
      setFormLoading(false);
    }
  };

  const fetchDevices = async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      const response = await getDevicesByProjectId(parseInt(projectId));
      if (response.data.code === 0 && response.data.data) {
        setDevices(response.data.data);
        setFilteredDevices(response.data.data);
      } else {
        message.error(response.data.msg || "获取设备列表失败");
      }
    } catch (error) {
      console.error("获取设备列表错误:", error);
      message.error("获取设备列表失败，请检查网络连接后重试");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchDevices();
    }
  }, [projectId]);

  // Filter devices based on search value and selected device type
  useEffect(() => {
    let result = [...devices];

    // Filter by device ID or driver name if search value exists
    if (searchValue) {
      result = result.filter(
        (device) =>
          device.deviceCode.toLowerCase().includes(searchValue.toLowerCase()) ||
          (device.exData?.driverName &&
            device.exData.driverName
              .toLowerCase()
              .includes(searchValue.toLowerCase())) ||
          (device.exData?.idcardNumber &&
            device.exData.idcardNumber
              .toLowerCase()
              .includes(searchValue.toLowerCase()))
      );
    }

    // Filter by device type if selected
    if (selectedDeviceType !== null) {
      result = result.filter(
        (device) => device.deviceType === selectedDeviceType
      );
    }

    setFilteredDevices(result);
  }, [devices, searchValue, selectedDeviceType]);

  const handleAddDevice = () => {
    // 完全重置表单状态
    form.resetFields();
    
    // 重置编辑设备状态 - 设置为 null 而不是 undefined
    setEditingDevice(null);
    
    // 重置选中的摄像头 - 确保是空数组而不是 undefined
    setSelectedCameras([]);
    
    // 使用当前选中的设备类型，或默认为 0
    const initialDeviceType = selectedDeviceType !== null ? selectedDeviceType : 0;
    
    // 处理区域值确保是数字
    const areaValue = projectArea ? 
      (typeof projectArea === 'object' ? (projectArea as any).id : projectArea) 
      : 0;
    
    // 明确设置空的默认值对象
    const defaultValues = {
      deviceCode: "",
      deviceName: "",
      deviceType: initialDeviceType,
      isPay: 0,
      area: areaValue,
      remark: "",
      deviceFactory: "",
      thirdType: "",
      beian: "",
      latitude: "",
      longitude: "",
      price: null,
      device_type: initialDeviceType,
      exData: {},
      appKey: undefined,
    };
    
    // 重置初始值状态
    setInitialValues(defaultValues);
    
    // 获取表单配置
    if (areaValue) {
      fetchFormConfig(areaValue, initialDeviceType);
    }
    
    // 强制清理表单中的所有字段
    form.resetFields();
    
    // 使用字段级别的 setFields 明确重置萤石云相关字段
    form.setFields([
      { name: 'exData', value: {} },
      { name: 'appKey', value: undefined },
      // 如果有其他萤石云专用字段，也在这里重置
      { name: 'channel', value: undefined },
      { name: 'deviceSerial', value: undefined },
    ]);
    
    // 延迟设置表单值，确保重置生效
    setTimeout(() => {
      form.setFieldsValue(defaultValues);
    }, 10);
    
    // 显示模态框
    setModalTitle("新增设备");
    setModalVisible(true);
  };

  const handleEditDevice = (device: Device) => {
    setModalTitle("编辑设备");
    setEditingDevice(device);

    // Extract area ID from device area (it could be an object or a number)
    const areaId = typeof device.area === 'object' && device.area !== null ? (device.area as {id: number}).id : device.area;

    // Prepare initial values for the form
    const values: Record<string, any> = {
      // Map API field names to form field names if using dynamic form
      device_code: device.deviceCode,
      device_name: device.deviceName,
      device_type: device.deviceType,
      // Keep original field names for backward compatibility
      deviceCode: device.deviceCode,
      deviceName: device.deviceName,
      deviceType: device.deviceType,
      deviceFactory: device.deviceFactory || "",
      thirdType: device.thirdType || "",
      beian: device.beian || "",
      latitude: device.latitude || "",
      longitude: device.longitude || "",
      price: device.price || null,
      isPay: device.isPay || 0,
      remark: device.remark || "",
      area: areaId || projectArea || 0,
    };

    // Add exData fields if they exist
    if (device.exData) {
      // First, add all exData fields except 'channel'
      Object.keys(device.exData).forEach((key) => {
        if (key !== 'channel') { // Skip channel array, it's handled separately by YingshiIntegration
          values[key] = device.exData[key];
        }
      });
      
      // Log exData fields for debugging
      console.log('Device exData:', device.exData);
      console.log('Form values after adding exData:', values);
    }

    // Reset initialValues state
    setInitialValues(values);
    
    // Fetch form configuration for this device type
    const area = areaId || projectArea || 0;
    fetchFormConfig(area, device.deviceType);
    
    // Set form values after fetching form configuration
    setTimeout(() => {
      form.setFieldsValue(values);
      
      // For video devices, ensure all form fields are properly set
      if (device.deviceType === 3 && device.exData) {
        // Force update all exData fields again to ensure they're set
        Object.keys(device.exData).forEach((key) => {
          if (key !== 'channel') {
            form.setFields([{
              name: key,
              value: device.exData[key]
            }]);
          }
        });
      }
    }, 0);
    
    setModalVisible(true);
  };

  const handleDeleteDevice = async (id: number) => {
    try {
      const response = await deleteDevice(id);
      if (response.data.code === 0) {
        message.success("设备删除成功");
        fetchDevices();
      } else {
        message.error(response.data.msg || "设备删除失败");
      }
    } catch (error) {
      console.error("设备删除错误:", error);
      message.error("设备删除失败，请检查网络连接后重试");
    }
  };

  const handleSubmit = async (values: Record<string, any>) => {
    try {
      // Extract standard fields
      const { 
        deviceCode, 
        deviceName, 
        deviceType, 
        deviceFactory, 
        thirdType, 
        beian, 
        latitude, 
        longitude, 
        price, 
        isPay, 
        remark,
        area: _area,  // Ignore area from form values
        exData,  // Extract existing exData if present
        ...otherFields  // Get remaining fields
      } = values;

      // Ensure projectId is a number and is valid
      const projectIdNum = projectId ? parseInt(projectId) : 0;
      
      // Ensure area is a valid number
      const areaNum = projectArea ? (typeof projectArea === 'object' ? (projectArea as any).id : projectArea) : 0;

      // Create base device data with standard fields
      const deviceData: any = {
        deviceCode,
        deviceName,
        deviceType,
        deviceFactory: deviceFactory || "",
        thirdType: thirdType || "",
        beian: beian || "",
        latitude: latitude || "",
        longitude: longitude || "",
        price: price || 0,
        isPay: isPay || 0,
        remark: remark || "",
        project: projectIdNum,  // Ensure project ID is set correctly
        area: areaNum,  // Ensure area is set correctly
      };

      // Log key values for debugging
      console.log('Project ID:', projectIdNum);
      console.log('Area:', areaNum);

      // Handle exData properly to prevent nesting
      if (deviceType === 3) {
        // For video devices, preserve the exData structure from DeviceForm
        deviceData.exData = values.exData;
      } else {
        // For other devices, include remaining fields in exData
        deviceData.exData = {
          ...otherFields,
          ...(exData && !exData.exData ? exData : {}),  // Include existing exData if it's not nested
        };
      }

      // Log the final device data for debugging
      console.log('Final device data:', deviceData);

      let response;
      if (editingDevice) {
        // Update existing device
        response = await updateDevice(editingDevice.id, deviceData);
      } else {
        // Create new device - add specific logging for debugging
        console.log('Creating new device with:');
        console.log('Project ID:', deviceData.project, 'Type:', typeof deviceData.project);
        console.log('Area:', deviceData.area, 'Type:', typeof deviceData.area);
        
        response = await createDevice(deviceData);
      }

      if (response.data.code === 0) {
        message.success(editingDevice ? "设备更新成功" : "设备创建成功");
        setModalVisible(false);
        // Reset form and editing state
        form.resetFields();
        setEditingDevice(null);
        // Reset selected cameras
        setSelectedCameras([]);
        // Refresh device list
        fetchDevices();
      } else {
        message.error(
          response.data.msg || (editingDevice ? "设备更新失败" : "设备创建失败")
        );
      }
    } catch (error) {
      console.error("表单提交错误:", error);
      message.error("表单提交失败，请检查输入内容");
    }
  };

  const handleDynamicFormCancel = () => {
    setModalVisible(false);
    
    // 重置表单状态
    form.resetFields();
    
    // 重置编辑设备状态
    setEditingDevice(null);
    
    // 重置选中的摄像头
    setSelectedCameras([]);
    
    // 清空 exData 对象
    form.setFields([{
      name: 'exData',
      value: {},
    }]);
    
    // 清空 appKey
    form.setFields([{
      name: 'appKey',
      value: undefined,
    }]);
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleDeviceTypeChange = (value: string | null) => {
    // Convert string key to number or null for "all" tab
    const deviceType = value === "all" ? null : parseInt(value as string);
    setSelectedDeviceType(deviceType);
  };

  const handleResetFilters = () => {
    setSearchValue("");
    setSelectedDeviceType(null);
    setFilteredDevices(devices);
  };

  // Handle device type change in the form
  const handleDeviceTypeFormChange = (value: number) => {
    // 获取当前区域
    const currentArea = form.getFieldValue('area') || projectArea || 0;
    
    // 获取当前设备类型
    const currentDeviceType = form.getFieldValue('deviceType');
    
    // 如果设备类型真的变化了
    if (currentDeviceType !== value) {
      // 无论如何都重置萤石云相关数据，确保干净的状态
      setSelectedCameras([]);
      
      // 清空 exData 对象和相关字段
      form.setFields([
        { name: 'exData', value: {} },
        { name: 'appKey', value: undefined },
        { name: 'channel', value: undefined },
        { name: 'deviceSerial', value: undefined }
      ]);
      
      // 如果切换到视频设备类型，需要稍微延迟再获取表单配置
      // 这是为了确保 YingshiIntegration 组件能正确初始化
      if (value === 3) {
        setTimeout(() => {
          fetchFormConfig(currentArea, value);
        }, 100);
      } else {
        // 其他设备类型直接获取表单配置
        fetchFormConfig(currentArea, value);
      }
    }
  };

  // Handle viewing live data for a device
  const handleViewLiveData = async (device: Device) => {
    if (!projectRegion) {
      message.error("项目区域信息不存在，无法查询实时数据");
      return;
    }

    setCurrentDevice(device);
    setLiveDataLoading(true);
    setLiveDataModalVisible(true);
    setLiveData(null);

    try {
      const response = await getDeviceLiveData(projectRegion, device.deviceCode);
      if (response.data.code === 0 && response.data.data) {
        setLiveData(response.data.data);
      } else {
        message.error(response.data.msg || "获取实时数据失败");
      }
    } catch (error) {
      console.error("获取实时数据错误:", error);
      message.error("获取实时数据失败，请检查网络连接后重试");
    } finally {
      setLiveDataLoading(false);
    }
  };

  // Handle camera selection from YingshiIntegration component
  const handleCamerasSelected = (cameras: YingshiCamera[]) => {
    setSelectedCameras(cameras);
  };

  // Handle device action menu click
  const handleDeviceActionClick = async (action: DeviceAction, device: Device) => {
    // Construct the base URL without appending the device code
    const baseUrl = action.action;
    const actionPath = action.url;
    
    // Ensure there's a slash between action and url if needed
    let fullUrl = baseUrl;
    if (!baseUrl.endsWith('/') && !actionPath.startsWith('/')) {
      fullUrl += '/';
    }
    
    // Add the action path
    fullUrl += actionPath;
    
    // Determine the HTTP method (default to GET if not specified)
    const method = action.method?.toLowerCase() || 'get';
    
    // For both GET and POST requests, show a confirmation dialog
    Modal.confirm({
      title: '确认操作',
      content: `确定要执行"${action.title}"操作吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          // Show loading message
          message.loading({ content: '正在处理请求...', key: 'actionRequest' });
          
          if (method === 'post') {
            // For POST requests, send device code in the request body
            const response = await fetch(fullUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                // Add authorization header if needed
                ...(localStorage.getItem('token') ? { 'Authorization': `Bearer ${localStorage.getItem('token')}` } : {})
              },
              body: JSON.stringify({ 
                id: device.id,  // Add device ID
                deviceCode: device.deviceCode,
                // Include other device information that might be useful
                deviceName: device.deviceName,
                deviceType: device.deviceType,
                deviceId: device.id,
                projectId: device.project,
                area: (typeof device.area === 'object' && device.area !== null ? (device.area as {id: number}).id : device.area),
                // Include any custom parameters defined in the action
                ...action.params,
                // Include any exData fields that might be useful
                ...(device.exData ? { exData: device.exData } : {})
              })
            });
            
            // Handle response
            handleActionResponse(response);
          } else {
            // For GET requests, append the device code to the URL
            let getUrl = fullUrl;
            if (!getUrl.endsWith('/')) {
              getUrl += '/';
            }
            getUrl += device.id;
            
            // Add any custom query parameters if defined in the action
            const queryParams = new URLSearchParams();
            // Add basic device information
            queryParams.append('id', device.id.toString());  // Add device ID
            queryParams.append('deviceName', device.deviceName);
            queryParams.append('deviceType', device.deviceType.toString());
            queryParams.append('deviceId', device.id.toString());
            queryParams.append('projectId', device.project.toString());
            queryParams.append('area', (typeof device.area === 'object' && device.area !== null ? (device.area as {id: number}).id : device.area).toString());

            // Add any custom parameters if defined in the action
            if (action.params && Object.keys(action.params).length > 0) {
              Object.entries(action.params).forEach(([key, value]) => {
                queryParams.append(key, String(value));
              });
            }

            getUrl += `?${queryParams.toString()}`;
            
            // Make a GET request
            const response = await fetch(getUrl, {
              method: 'GET',
              headers: {
                // Add authorization header if needed
                ...(localStorage.getItem('token') ? { 'Authorization': `Bearer ${localStorage.getItem('token')}` } : {})
              }
            });
            
            // Handle response
            handleActionResponse(response);
          }
        } catch (error) {
          console.error('设备操作请求错误:', error);
          message.error({ content: '操作请求失败，请检查网络连接', key: 'actionRequest', duration: 2 });
        }
      }
    });
  };

  // Helper function to handle API responses
  const handleActionResponse = async (response: Response) => {
    if (response.ok) {
      // Try to parse as JSON first
      try {
        const data = await response.json();
        // Get message from either message or msg field
        const responseMessage = data.message || data.msg || '操作成功';
        
        // Check if the response has a code field (API standard format)
        if ('code' in data) {
          if (data.code === 0) {
            message.success({ content: responseMessage, key: 'actionRequest', duration: 2 });
            
            // Check if the response includes a redirect URL
            if (data.data && data.data.redirectUrl) {
              // Ask user if they want to navigate to the URL
              Modal.confirm({
                title: '操作成功',
                content: '是否跳转到结果页面？',
                okText: '跳转',
                cancelText: '留在当前页面',
                onOk: () => {
                  window.open(data.data.redirectUrl, '_blank');
                }
              });
            } else {
              // Refresh device list after successful operation
              fetchDevices();
            }
          } else {
            message.error({ content: responseMessage, key: 'actionRequest', duration: 2 });
          }
        } else {
          // No code field, just show success
          message.success({ content: responseMessage, key: 'actionRequest', duration: 2 });
          // Refresh device list after successful operation
          fetchDevices();
        }
      } catch (e) {
        // Not JSON, just show success
        message.success({ content: '操作成功', key: 'actionRequest', duration: 2 });
        // Refresh device list after successful operation
        fetchDevices();
      }
    } else {
      try {
        const errorData = await response.json();
        // Get error message from either message or msg field
        const errorMessage = errorData.message || errorData.msg || `操作失败 (${response.status})`;
        message.error({ content: errorMessage, key: 'actionRequest', duration: 2 });
      } catch (e) {
        // Not JSON, show status text
        message.error({ content: `操作失败: ${response.statusText || response.status}`, key: 'actionRequest', duration: 2 });
      }
    }
  };

  // Add this new function to handle video playback
  const handlePlayVideo = async (device: Device) => {
    // Check if device has necessary video information
    if (!device.exData || !device.exData.channel || !device.exData.appKey) {
      message.error("设备缺少视频信息，无法播放");
      return;
    }
    
    try {
      // Just set the modal visible and pass the device to the VideoPlayer component
      setVideoLoading(false); // No need for loading state here anymore
      setVideoModalVisible(true);
      setSelectedVideoDevice(device);
      // We don't need to fetch the stream URL anymore as the VideoPlayer component will handle it
      setVideoStreamUrl(null);
    } catch (error) {
      console.error("视频播放准备错误:", error);
      message.error("视频播放准备失败，请检查网络连接后重试");
    }
  };

  // Handle viewing logs for a device
  const handleViewLogs = async (device: Device) => {
    // Get the area ID from device
    const areaId = typeof device.area === 'object' && device.area !== null ? 
      (device.area as {id: number}).id : 
      (typeof device.area === 'number' ? device.area : 0);
    
    if (!areaId) {
      message.error("设备区域信息不存在，无法查询日志");
      return;
    }

    setCurrentDevice(device);
    setLogsLoading(true);
    setLogsModalVisible(true);
    setDeviceLogs([]);

    try {
      const response = await getDeviceLogs(areaId, device.deviceCode);
      if (response.data.code === 0) {
        // Handle both array and single object formats
        if (Array.isArray(response.data.data)) {
          setDeviceLogs(response.data.data);
        } else if (response.data.data && typeof response.data.data === 'object' && 'items' in response.data.data) {
          setDeviceLogs(response.data.data.items);
        } else if (response.data.data && typeof response.data.data === 'object') {
          // Handle single log entry format with type casting
          const logData = response.data.data as {
            device_code: string;
            action: string;
            data: string;
            status: number;
            add_time: string;
          };
          
          // Create a properly typed log entry
          const logEntry: DeviceLog = {
            id: Date.now(),
            device_code: logData.device_code,
            action: logData.action,
            data: logData.data,
            status: logData.status,
            add_time: logData.add_time,
            // Add compatibility fields
            logTime: logData.add_time,
            type: logData.status === 0 ? 'info' : 'error',
            content: `操作: ${logData.action}\n响应: ${logData.data}`,
            deviceCode: logData.device_code
          };
          
          setDeviceLogs([logEntry]);
        }
      } else {
        message.error(response.data.msg || "获取日志数据失败");
      }
    } catch (error) {
      console.error("获取日志数据错误:", error);
      message.error("获取日志数据失败，请检查网络连接后重试");
    } finally {
      setLogsLoading(false);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 100,
    },
    {
      title: "区域/添加时间",
      key: "areaAndTime",
      width: 160,
      render: (_: any, record: Device) => {
        // Get area title from the area object or use default
        let areaTitle = '未知区域';
        if (record.area) {
          if (typeof record.area === 'object' && record.area !== null && 'title' in record.area) {
            areaTitle = (record.area as {title: string}).title;
          } else if (typeof record.area === 'number') {
            areaTitle = `区域${record.area}`;
          }
        }
        
        return (
          <div>
            <div>
              {areaTitle}
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              {record.addTime ? new Date(record.addTime).toLocaleString() : "未知"}
            </div>
          </div>
        );
      },
    },
    {
      title: "设备ID/类型",
      key: "deviceInfo",
      render: (_: any, record: Device) => (
        <Space>
          <Tag color="blue">{record.deviceCode}</Tag>
          <Tag color="purple">
            {deviceTypeMap[record.deviceType as keyof typeof deviceTypeMap] || "未知"}
          </Tag>
        </Space>
      ),
    },
    {
      title: "设备名称",
      dataIndex: "deviceName",
      key: "deviceName",
    },
    {
      title: "是否结算",
      dataIndex: "isPay",
      key: "isPay",
      width: 90,
      render: (isPay: number) => isPay === 1 ? 
        <Tag color="green">已结算</Tag> : 
        <Tag color="red">未结算</Tag>,
    },
    {
      title: "操作",
      key: "action",
      width: 280,
      render: (_: any, record: Device) => {
        // Filter actions for this device type
        const availableActions = deviceActions.filter(
          action => action.deviceType === record.deviceType
        );
        
        // Create base actions array with Edit and Delete
        const baseActions = [
          {
            key: 'edit',
            label: '编辑',
            icon: <EditOutlined />,
            onClick: () => handleEditDevice(record)
          },
          {
            key: 'delete',
            label: '删除',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: "确定要删除此设备吗？",
                onOk: () => handleDeleteDevice(record.id),
                okText: "确定",
                cancelText: "取消",
              });
            }
          }
        ];
        
        // Combine base actions with device-specific actions
        const allActions = [
          ...baseActions,
          ...availableActions.map(action => ({
            key: action.id.toString(),
            label: action.title,
            onClick: () => handleDeviceActionClick(action, record)
          }))
        ];
        
        return (
          <Space size="middle">
            {record.deviceType !== 3 && (
              <Button
                type="text"
                icon={<LineChartOutlined />}
                onClick={() => handleViewLiveData(record)}
              >
                实时数据
              </Button>
            )}
            
            {/* Add Logs Button for all device types */}
            <Button
              type="text"
              icon={<CloudOutlined />}
              onClick={() => handleViewLogs(record)}
            >
              上报日志
            </Button>
            
            {/* Add Video Button for video devices (type 3) */}
            {record.deviceType === 3 && (
              <Button
                type="text"
                icon={<VideoCameraOutlined />}
                onClick={() => handlePlayVideo(record)}
              >
                播放视频
              </Button>
            )}
            
            {/* Operations Dropdown Menu - Always show since it now contains Edit and Delete */}
            <Dropdown
              menu={{
                items: allActions
              }}
              trigger={['click']}
              overlayClassName="operations-dropdown"
            >
              <Button type="text" icon={<MoreOutlined />} loading={actionsLoading} className="operations-button">
                操作
              </Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  // Reset form and state when component unmounts or projectId changes
  useEffect(() => {
    return () => {
      form.resetFields();
      setEditingDevice(null);
      setInitialValues({});
      setSelectedCameras([]);
    };
  }, [form, projectId]);

  return (
    <div className="devices-container">
      <Card className="devices-card">
        <div className="devices-header">
          <Title level={4}>
            {projectName ? `${projectName} - 设备列表` : "设备列表"}
          </Title>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddDevice}
            >
              新增设备
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchDevices}>
              刷新
            </Button>
          </Space>
        </div>

        {/* Filter and Search Section */}
        <div className="devices-filter-section">
          <Row gutter={16} align="middle" style={{ marginBottom: '16px' }}>
            <Col xs={24} sm={12} md={10} lg={8}>
              <Search
                placeholder="搜索设备ID/驾驶员/身份证号"
                allowClear
                enterButton={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={handleSearch}
              />
            </Col>
            <Col xs={24} sm={12} md={10} lg={8}>
              <Button icon={<FilterOutlined />} onClick={handleResetFilters}>
                重置筛选
              </Button>
            </Col>
          </Row>
        </div>

        <div className="table-container">
           <Tabs
            activeKey={selectedDeviceType !== null ? selectedDeviceType.toString() : "all"}
            onChange={handleDeviceTypeChange}
            className="device-tabs"
            items={[
              {
                key: "all",
                label: (
                  <span>
                    全部设备
                    <Tag style={{ marginLeft: 8 }}>{devices.length}</Tag>
                  </span>
                ),
              },
              ...Object.entries(deviceTypeMap).map(([key, value]) => {
                // Count devices of this type
                const count = devices.filter(device => device.deviceType === parseInt(key)).length;
                return {
                  key: key,
                  label: (
                    <span>
                      {value}
                      <Tag style={{ marginLeft: 8 }}>{count}</Tag>
                    </span>
                  ),
                };
              })
            ]}
          />
          <Spin spinning={loading}>
            <Table
              dataSource={filteredDevices}
              columns={columns}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                pageSize: 10,
              }}
              size="middle"
              bordered={false}
              className="devices-table"
            />
          </Spin>
        </div>
      </Card>

      {/* Device Form Modal */}
      <Modal
        title={
          <div style={{ fontSize: "18px", fontWeight: 500 }}>
            {modalTitle}
            <span
              style={{
                fontSize: "14px",
                fontWeight: 400,
                marginLeft: "8px",
                color: "#8c8c8c",
              }}
            >
              {projectName ? `(${projectName})` : ""}
            </span>
          </div>
        }
        open={modalVisible}
        onCancel={handleDynamicFormCancel}
        footer={null}
        width={800}
        styles={{ body: { maxHeight: "70vh", overflow: "auto", padding: "24px" } }}
      >
        <Spin spinning={formLoading}>
          <DeviceForm
            form={form}
            initialValues={initialValues}
            onFinish={handleSubmit}
            onCancel={handleDynamicFormCancel}
            isEditing={!!editingDevice}
            formConfig={formConfig}
            onDeviceTypeChange={handleDeviceTypeFormChange}
            editingDevice={editingDevice}
            key={`device-form-${editingDevice?.id || 'new'}-${Date.now()}`}
          />
        </Spin>
      </Modal>

      {/* Live Data Modal */}
      <Modal
        title={
          <div style={{ fontSize: "18px", fontWeight: 500 }}>
            设备实时数据
            <span
              style={{
                fontSize: "14px",
                fontWeight: 400,
                marginLeft: "8px",
                color: "#8c8c8c",
              }}
            >
              {currentDevice ? `(${currentDevice.deviceName})` : ""}
            </span>
          </div>
        }
        open={liveDataModalVisible}
        onCancel={() => setLiveDataModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setLiveDataModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="refresh" 
            type="primary" 
            icon={<ReloadOutlined />}
            loading={liveDataLoading}
            onClick={() => currentDevice && handleViewLiveData(currentDevice)}
          >
            刷新数据
          </Button>
        ]}
        width={800}
        styles={{ body: { maxHeight: "70vh", overflow: "auto", padding: "24px" } }}
      >
        <Spin spinning={liveDataLoading}>
          {liveData ? (
            <div className="live-data-container">
              {liveData.DateTime && (
                <div style={{ marginBottom: '16px', fontSize: '14px', color: '#1890ff' }}>
                  最新数据时间: {liveData.DateTime}
                </div>
              )}
              <Table 
                dataSource={Object.entries(liveData)
                  .filter(([key]) => key !== 'DateTime')
                  .map(([key, value]) => {
                    // 如果值是数组，只取第一条
                    const processedValue = Array.isArray(value) ? value[0] : value;
                    return { 
                      key, 
                      name: key, 
                      value: typeof processedValue === 'object' ? 
                        JSON.stringify(processedValue, null, 2) : 
                        String(processedValue) 
                    };
                  })}
                columns={[
                  { 
                    title: '数值', 
                    dataIndex: 'value', 
                    key: 'value',
                    render: (text) => (
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {text}
                      </div>
                    )
                  }
                ]}
                pagination={false}
                size="small"
                bordered
              />
            </div>
          ) : (
            <div className="no-data-message">
              {liveDataLoading ? "正在加载数据..." : "暂无实时数据"}
            </div>
          )}
        </Spin>
      </Modal>

      {/* Video Player Modal */}
      <Modal
        title={
          <div style={{ fontSize: "18px", fontWeight: 500 }}>
            视频监控
            <span
              style={{
                fontSize: "14px",
                fontWeight: 400,
                marginLeft: "8px",
                color: "#8c8c8c",
              }}
            >
              {selectedVideoDevice ? `(${selectedVideoDevice.deviceName})` : ""}
            </span>
          </div>
        }
        open={videoModalVisible}
        onCancel={() => setVideoModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setVideoModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
        styles={{ body: { padding: "0" } }}
      >
        <Spin spinning={videoLoading}>
          {selectedVideoDevice && selectedVideoDevice.exData ? (
            <VideoPlayer 
              appKey={selectedVideoDevice.exData.appKey}
              deviceSerial={selectedVideoDevice.exData.channel?.[0]?.deviceSerial}
              channels={selectedVideoDevice.exData.channel}
              width="100%"
              height="500px"
            />
          ) : videoStreamUrl ? (
            <VideoPlayer url={videoStreamUrl} />
          ) : (
            <div style={{ height: "400px", display: "flex", justifyContent: "center", alignItems: "center" }}>
              {videoLoading ? "正在加载视频流..." : "暂无可用视频流"}
            </div>
          )}
        </Spin>
      </Modal>

      {/* Logs Modal */}
      <Modal
        title={
          <div style={{ fontSize: "18px", fontWeight: 500 }}>
            设备上报日志
            <span
              style={{
                fontSize: "14px",
                fontWeight: 400,
                marginLeft: "8px",
                color: "#8c8c8c",
              }}
            >
              {currentDevice ? `(${currentDevice.deviceName})` : ""}
            </span>
          </div>
        }
        open={logsModalVisible}
        onCancel={() => setLogsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setLogsModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="refresh" 
            type="primary" 
            icon={<ReloadOutlined />}
            loading={logsLoading}
            onClick={() => currentDevice && handleViewLogs(currentDevice)}
          >
            刷新日志
          </Button>
        ]}
        width={800}
        styles={{ body: { maxHeight: "70vh", overflow: "auto", padding: "24px" } }}
      >
        <Spin spinning={logsLoading}>
          {deviceLogs && deviceLogs.length > 0 ? (
            <div className="device-logs-container">
              <Table 
                dataSource={deviceLogs.map((log, index) => ({
                  ...log,
                  key: log.id || index
                }))}
                columns={[
                  { 
                    title: '时间', 
                    dataIndex: 'add_time', 
                    key: 'time',
                    width: 180,
                    render: (text, record) => {
                      // Use add_time if available, otherwise fallback to logTime
                      const timeValue = text || record.logTime;
                      return timeValue ? new Date(timeValue).toLocaleString() : '-';
                    }
                  },
                  { 
                    title: '操作/接口', 
                    dataIndex: 'action', 
                    key: 'action',
                    width: 180,
                    ellipsis: true,
                    render: (text) => text || '-'
                  },
                  { 
                    title: '状态', 
                    dataIndex: 'status', 
                    key: 'status',
                    width: 80,
                    render: (status, record) => {
                      // Support both old and new formats
                      if (status !== undefined) {
                        return (
                          <Tag color={status === 0 ? 'green' : 'red'}>
                            {status === 0 ? '成功' : '失败'}
                          </Tag>
                        );
                      } else if (record.type) {
                        return (
                          <Tag color={
                            record.type === 'error' ? 'red' : 
                            record.type === 'warning' ? 'orange' : 
                            record.type === 'info' ? 'blue' : 'default'
                          }>
                            {record.type || '未知'}
                          </Tag>
                        );
                      }
                      return <Tag>未知</Tag>;
                    }
                  },
                  { 
                    title: '响应数据', 
                    dataIndex: 'data', 
                    key: 'data',
                    render: (text, record) => {
                      // Use data if available, otherwise fallback to content
                      const content = text || record.content;
                      return (
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
                        </div>
                      );
                    }
                  }
                ]}
                pagination={{ pageSize: 10 }}
                size="small"
                bordered
              />
            </div>
          ) : (
            <div className="no-data-message" style={{ textAlign: 'center', padding: '40px 0' }}>
              {logsLoading ? "正在加载日志..." : "暂无上报日志"}
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default Devices;
